import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, Save, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle, Send, Users2, Bell } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FIELDS = [
  { key: 'SMTP_HOST', label: 'SMTP Host', placeholder: 'π.χ. smtp.gmail.com' },
  { key: 'SMTP_PORT', label: 'SMTP Port', placeholder: '587' },
  { key: 'SMTP_USERNAME', label: 'SMTP Username', placeholder: 'email@courtplan.gr' },
  { key: 'SMTP_PASSWORD', label: 'SMTP Password / App Key', secret: true },
  { key: 'SMTP_FROM', label: 'From Email', placeholder: 'no-reply@courtplan.gr' },
  { key: 'SMTP_FROM_NAME', label: 'From Name', placeholder: 'COURTPLAN' },
  { key: 'SMTP_USE_TLS', label: 'STARTTLS (port 587)', toggle: true },
  { key: 'SMTP_ENABLED', label: 'Ενεργοποίηση αποστολής email', toggle: true },
];

export default function EmailSettings({ users = [] }) {
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [secretVisible, setSecretVisible] = useState(false);
  const [effectiveEnabled, setEffectiveEnabled] = useState(false);
  const [secretIsSet, setSecretIsSet] = useState(false);

  // Compose state
  const [mode, setMode] = useState('single'); // single | selected | broadcast
  const [singleEmail, setSingleEmail] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [remindersRunning, setRemindersRunning] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/settings/email`, { withCredentials: true });
      setEffectiveEnabled(!!data._effective_enabled);
      setSecretIsSet(!!data.SMTP_PASSWORD_set);
      const v = {};
      FIELDS.forEach((f) => { v[f.key] = data[f.key] ?? ''; });
      setValues(v);
    } catch (err) {
      toast.error('Αποτυχία φόρτωσης SMTP');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleChange = (k, val) => setValues((p) => ({ ...p, [k]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...values };
      if (payload.SMTP_PASSWORD && payload.SMTP_PASSWORD.includes('••••')) delete payload.SMTP_PASSWORD;
      await axios.put(`${API}/admin/settings/email`, payload, { withCredentials: true });
      toast.success('SMTP αποθηκεύτηκε');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Αποτυχία');
    } finally { setSaving(false); }
  };

  const toggleUser = (uid) => {
    setSelectedUserIds((prev) => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error('Συμπλήρωσε θέμα και κείμενο');
      return;
    }
    const payload = { subject, body };
    if (mode === 'single') {
      if (!singleEmail.trim()) { toast.error('Δώσε email παραλήπτη'); return; }
      payload.email = singleEmail.trim().toLowerCase();
    } else if (mode === 'broadcast') {
      if (!window.confirm(`Αποστολη σε όλους τους ${users.length} χρήστες;`)) return;
      payload.broadcast = true;
    } else {
      if (selectedUserIds.length === 0) { toast.error('Επέλεξε τουλάχιστον έναν χρήστη'); return; }
      payload.user_ids = selectedUserIds;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/email/send`, payload, { withCredentials: true });
      toast.success(`Στάλθηκαν: ${data.sent} · Αποτυχίες: ${data.failed} · Skipped: ${data.skipped}`);
      setSubject(''); setBody(''); setSingleEmail(''); setSelectedUserIds([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Αποτυχία');
    } finally { setSending(false); }
  };

  const handleSendReminders = async () => {
    setRemindersRunning(true);
    try {
      const { data } = await axios.post(`${API}/admin/tasks/send_expiration_reminders?days_before=3`, {}, { withCredentials: true });
      toast.success(`Υπενθυμίσεις: ${data.sent} στάλθηκαν / ${data.checked} ελέγχθηκαν`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Αποτυχία');
    } finally { setRemindersRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="email-admin">
      {/* SMTP Settings */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-slate-900 text-white flex items-center justify-center"><Mail className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Email · Ρυθμίσεις SMTP</h2>
            <p className="text-xs text-slate-500">Οι διαπιστεύσεις αποθηκεύονται με ασφάλεια στη βάση</p>
          </div>
          {effectiveEnabled ? (
            <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 px-2 py-1 rounded">
              <CheckCircle2 className="w-3 h-3" /> Ενεργο
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-1 rounded">
              <AlertTriangle className="w-3 h-3" /> Ανενεργο
            </span>
          )}
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Φόρτωση...</div>
        ) : (
          <div className="p-5 space-y-3">
            {FIELDS.map((f) => {
              if (f.toggle) {
                const on = values[f.key] === 'true';
                return (
                  <label key={f.key} className="flex items-center justify-between py-1.5 cursor-pointer">
                    <span className="text-sm font-medium text-slate-800">{f.label}</span>
                    <button
                      type="button"
                      onClick={() => handleChange(f.key, on ? 'false' : 'true')}
                      className={`relative w-12 h-6 rounded-full transition-colors ${on ? 'bg-green-600' : 'bg-slate-300'}`}
                      data-testid={`smtp-toggle-${f.key}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-all ${on ? 'left-6' : 'left-0.5'}`} />
                    </button>
                  </label>
                );
              }
              return (
                <div key={f.key}>
                  <label className="block text-sm font-medium text-slate-700 mb-1">{f.label}</label>
                  <div className="relative">
                    <input
                      type={f.secret && !secretVisible ? 'password' : 'text'}
                      value={values[f.key] || ''}
                      onChange={(e) => handleChange(f.key, e.target.value)}
                      placeholder={f.placeholder || ''}
                      className="w-full h-10 px-3 pr-10 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-red-700 focus:border-red-700"
                    />
                    {f.secret && (
                      <button type="button" onClick={() => setSecretVisible(!secretVisible)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600">
                        {secretVisible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                  {f.secret && secretIsSet && <div className="text-xs text-green-600 mt-1">(Υπάρχει αποθηκευμένος κωδικός — συμπλήρωσε μόνο για αντικατάσταση)</div>}
                </div>
              );
            })}
            <div className="flex items-center justify-end pt-1">
              <Button onClick={handleSave} disabled={saving} className="bg-red-700 hover:bg-red-800 text-white" data-testid="smtp-save-btn">
                <Save className="w-4 h-4 mr-1.5" />{saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Compose email */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="email-compose">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-100 text-red-700 flex items-center justify-center"><Send className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="font-semibold text-slate-900">Αποστολη email</h2>
            <p className="text-xs text-slate-500">Μεμονωμένα ή ομαδικά σε χρήστες</p>
          </div>
          <Button
            onClick={handleSendReminders}
            disabled={remindersRunning}
            variant="outline"
            size="sm"
            className="gap-1.5"
            data-testid="send-reminders-btn"
          >
            <Bell className="w-4 h-4" />
            {remindersRunning ? '...' : 'Αποστολή υπενθυμίσεων λήξης'}
          </Button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2" data-testid="email-mode-tabs">
            {[
              { id: 'single', label: 'Σε ένα email' },
              { id: 'selected', label: 'Σε επιλεγμένους' },
              { id: 'broadcast', label: 'Σε όλους' },
            ].map((m) => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`px-3 py-1.5 text-sm rounded-md ${mode === m.id ? 'bg-red-700 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
                data-testid={`email-mode-${m.id}`}
              >
                {m.label}
              </button>
            ))}
          </div>

          {mode === 'single' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email παραλήπτη</label>
              <input
                type="email"
                value={singleEmail}
                onChange={(e) => setSingleEmail(e.target.value)}
                placeholder="someone@example.com"
                className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm"
                data-testid="email-single-input"
              />
            </div>
          )}

          {mode === 'selected' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1 flex items-center gap-2">
                <Users2 className="w-4 h-4" /> Επιλεγμένοι χρήστες ({selectedUserIds.length})
              </label>
              <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100" data-testid="email-user-picker">
                {users.length === 0 && <div className="p-4 text-center text-sm text-slate-500">Δεν υπάρχουν χρήστες</div>}
                {users.map((u) => (
                  <label key={u._id} className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={selectedUserIds.includes(u._id)}
                      onChange={() => toggleUser(u._id)}
                      data-testid={`email-user-checkbox-${u._id}`}
                    />
                    <div className="flex-1 text-sm">
                      <div className="font-medium text-slate-800">{u.email}</div>
                      <div className="text-xs text-slate-500">{u.name || '—'}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          {mode === 'broadcast' && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              Θα σταλεί σε <strong>όλους ({users.length})</strong> τους χρήστες.
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Θέμα</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-10 px-3 border border-slate-300 rounded-md text-sm"
              data-testid="email-subject-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Κείμενο</label>
            <textarea
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm font-mono"
              placeholder="Το κείμενό σας... (νέες γραμμές γίνονται <br>)"
              data-testid="email-body-input"
            />
          </div>

          <div className="flex justify-end">
            <Button onClick={handleSend} disabled={sending} className="bg-red-700 hover:bg-red-800 text-white" data-testid="email-send-btn">
              <Send className="w-4 h-4 mr-1.5" />
              {sending ? 'Αποστολή...' : 'Αποστολή'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
